import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";

import { Feature } from "geojson";
import { useResourceContext } from "react-admin";

import { useKmlResourceImportState } from "./useKmlResourceImportState";
import { getTextByLabelForResource } from "../helpers/getTextByLabelForResource";
import { FileUploadStep } from "../shared/FileUploadStep";
import { ImportStatusStep } from "../shared/ImportStatusStep";
import { ResourceReviewStep } from "../shared/ResourceReviewStep";
import { ResourcesImportButtons } from "../shared/ResourcesImportButtons";
import * as S from "../shared/styled";

export interface KmlResourceImportDialogProps<TCreateEntity> {
  isOpen: boolean;
  handleClose: () => void;
  handleAssertInput: (input: unknown) => asserts input is Feature[];
  handleFilterInput: (input: Feature[]) => Feature[];
  handleMapResourceToCreateEntity: (data: Feature[]) => TCreateEntity[];
}

export function KmlResourceImportDialog<TCreateEntity>(
  props: KmlResourceImportDialogProps<TCreateEntity>
) {
  const { isOpen } = props;
  const resource = useResourceContext();

  const { onClose, dialogState, onFileAdded, onFileConfirm, onSave } = useKmlResourceImportState(
    props.handleClose,
    props.handleAssertInput,
    props.handleFilterInput,
    props.handleMapResourceToCreateEntity
  );

  const activeStep = dialogState.step;

  return (
    <Dialog open={isOpen} onClose={onClose} sx={{ minWidth: "300" }}>
      <Box sx={{ p: 2 }}>
        <DialogTitle>{getTextByLabelForResource(resource, "title")}</DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel aria-label="Import steps">
            <Step>
              <StepLabel>Upload file</StepLabel>
            </Step>
            <Step>
              <StepLabel>{getTextByLabelForResource(resource, "review")}</StepLabel>
            </Step>
            <Step>
              <StepLabel>Save</StepLabel>
            </Step>
          </Stepper>

          {activeStep === 0 && (
            <FileUploadStep
              file={dialogState.file}
              accept={{
                "application/vnd.google-earth.kml+xml": [".kml"]
              }}
              message={dialogState.message}
              onFileAdded={onFileAdded}
            />
          )}

          {activeStep === 1 && (
            <ResourceReviewStep importedResources={dialogState.importedResources} />
          )}

          {activeStep === 2 && <ImportStatusStep status={dialogState.status} />}
        </DialogContent>

        {activeStep === -1 && (
          <S.DialogContentContainer data-testid="incorrectKmlMessage">
            Provided KML file is invalid.
          </S.DialogContentContainer>
        )}

        <ResourcesImportButtons
          dialogState={dialogState}
          onClose={onClose}
          onFileConfirm={onFileConfirm}
          onSave={onSave}
        />
      </Box>
    </Dialog>
  );
}
